/**
 * Contract for the Mobiltex SSO Preference lookup used by the email-first login page
 * (SYRC-6097, Option B).
 *
 * The wire shape below mirrors the endpoint CorView already ships
 * (GET /sso-preference?email=...), which Vivek confirmed CorInsights may call.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * OPEN QUESTION — sso_provider encoding is NOT yet confirmed.
 *
 * Two authoritative sources disagree:
 *   - CorView Auth0 architecture page (Confluence): '1' = Microsoft, '2' = Okta,
 *     '3' = Custom SAML  (numeric-string codes)
 *   - CVC-4134 acceptance criteria: "microsoft", "okta"  (lowercase names)
 *
 * Until the live response is confirmed, `mapSsoProvider` accepts BOTH encodings.
 * When the real contract is pinned down, narrow `mapSsoProvider` — it is the ONLY
 * place that needs to change.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Raw response body, exactly as returned by the endpoint. Snake_case is the wire format. */
export interface SsoPreferenceResponse {
  sso_enabled: boolean;
  /** Provider code or name. See the OPEN QUESTION note above. */
  sso_provider?: string | number | null;
  /** Auth0 connection name to pass as the `connection` param when federating. */
  saml_connection_name?: string | null;
}

/**
 * Identity provider the user should be sent to, normalised away from the wire encoding.
 */
export enum SsoProvider {
  /** No SSO for this user's company — username/password via Auth0. */
  None = 'none',
  /** Microsoft / Azure AD / Office 365. */
  Microsoft = 'microsoft',
  /** Okta or a custom SAML enterprise connection. */
  Enterprise = 'enterprise',
  /**
   * SSO is enabled but the provider code was not recognised. Treated as a generic
   * SSO button rather than silently mis-routing the user to the wrong IdP.
   */
  Unknown = 'unknown',
}

/** Normalised, app-facing view of a successful lookup. */
export interface SsoPreference {
  ssoEnabled: boolean;
  provider: SsoProvider;
  /** Auth0 connection name, when the endpoint supplied one. */
  connectionName: string | null;
}

/** Why a lookup did not produce a usable preference. */
export enum SsoLookupStatus {
  /** Endpoint answered; `preference` is populated. */
  Found = 'found',
  /** Endpoint answered 404 — no CorInsights account for this email. */
  NotFound = 'not-found',
  /** Network failure, timeout, 5xx, or malformed body. */
  Error = 'error',
}

/**
 * Result of an SSO preference lookup.
 *
 * NotFound is modelled separately from Error on purpose: a 404 is a *successful*
 * answer meaning "no such account", whereas Error means "we could not ask".
 * The two warrant different UX, and conflating them is the gap currently present
 * in the SYRC-6097 acceptance criteria.
 */
export type SsoLookupResult =
  | { status: SsoLookupStatus.Found; preference: SsoPreference }
  | { status: SsoLookupStatus.NotFound }
  | { status: SsoLookupStatus.Error };

/**
 * Wire `sso_provider` values that map to Microsoft / Azure AD.
 *
 * Includes the Confluence numeric code ('1'), the CVC-4134 name ('microsoft'),
 * and the Auth0 connection aliases for the same IdP. Kept as data — not a switch —
 * so the two unconfirmed encodings can coexist and be narrowed in one place once
 * the live contract is known.
 */
const MICROSOFT_PROVIDER_VALUES: ReadonlySet<string> = new Set([
  '1',
  'microsoft',
  'azure',
  'azuread',
  'azure-ad',
  'waad',
  'office365',
]);

/** Wire `sso_provider` values that map to Okta / custom SAML enterprise connections. */
const ENTERPRISE_PROVIDER_VALUES: ReadonlySet<string> = new Set([
  '2',
  '3',
  'okta',
  'saml',
  'samlp',
  'enterprise',
  'custom',
]);

/**
 * Normalises the wire `sso_provider` value to an {@link SsoProvider}.
 *
 * Deliberately tolerant of both known encodings while the contract is unconfirmed.
 * Narrow the two sets above once the live response is verified.
 */
export function mapSsoProvider(raw: string | number | null | undefined): SsoProvider {
  if (raw === null || raw === undefined || raw === '') {
    return SsoProvider.None;
  }

  const value = String(raw).trim().toLowerCase();

  if (MICROSOFT_PROVIDER_VALUES.has(value)) {
    return SsoProvider.Microsoft;
  }

  if (ENTERPRISE_PROVIDER_VALUES.has(value)) {
    return SsoProvider.Enterprise;
  }

  // Enabled but unrecognised: surface a generic SSO option instead of guessing.
  return SsoProvider.Unknown;
}

/**
 * Converts a raw response into the normalised app model.
 *
 * `sso_enabled: false` wins over any provider value — a disabled company always
 * takes the password path regardless of what provider is recorded against it.
 */
export function normalizeSsoPreference(
  response: SsoPreferenceResponse,
): SsoPreference {
  if (!response?.sso_enabled) {
    return {
      ssoEnabled: false,
      provider: SsoProvider.None,
      connectionName: null,
    };
  }

  return {
    ssoEnabled: true,
    provider: mapSsoProvider(response.sso_provider),
    connectionName: response.saml_connection_name?.trim() || null,
  };
}
